import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import {
	TextField,
	Dialog,
	DialogActions,
	DialogContent,
	Button,
	Icon,
	Zoom,
	FormHelperText,
	CircularProgress,
	InputAdornment,
	IconButton,
	FormControlLabel,
	Checkbox,
	makeStyles,
	Backdrop
} from '@material-ui/core';
import { GoogleOAuthProvider } from '@react-oauth/google';

import FacebookLoginBtn from '../auth/FacebookLoginBtn';
import GoogleLoginBtn from '../auth/GoogleLoginBtn';
import { login } from '../../store/slices/auth';
import { clearMessage } from '../../store/slices/message';
import { clearError } from '../../store/slices/errors';
import ErrorValidationAlert from '../alerts/ErrorValidationAlert';
import ErrorMessage from '../alerts/ErrorMessage';

import logo from '../../assets/images/logos/mulk-160x80.png';

const useStyles = makeStyles(theme => ({
    formControlLabel: {
        fontSize: '14px'
    },
	backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
}));

const formSchema = Yup.object().shape({
	login: Yup.string().required('This field is required.'),
	password: Yup.string().required('This field is required.'),
	terms: Yup.boolean().oneOf([true], 'This is a required field.')
});

const GOOGLE_APP_ID = process.env.REACT_APP_GOOGLE_APP_ID;

const LoginDialog = (props) => {

	const classes = useStyles();
	const dispatch = useDispatch();
	const { errors } = useSelector((state) => state.errors);
	const { message } = useSelector((state) => state.message);
	const [open, setOpen] = useState(false);
	const [values, setValues] = useState({
    	password: '',
    	showPassword: false,
  	});

  	const handleClickShowPassword = () => {
    	setValues({ showPassword: !values.showPassword });
  	}

  	const handleMouseDownPassword = (event) => {
    	event.preventDefault();
  	}

	const handleSubmit = (values, formikBag) => {

		dispatch(clearMessage());
        dispatch(clearError());

		setOpen(true)

    	const { setSubmitting, resetForm } = formikBag;	 
		
		const formData = new FormData();
        formData.append('login', values.login);
        formData.append('password', values.password);

		dispatch(login(formData))
            .unwrap()
            .then((res) => {
				setOpen(false)
                setSubmitting(false)
				resetForm(true)
				if(res.token) {
					props.setDialogIsOpen(false)
					window.location.reload();
				}
            })
            .catch(() => {
				setOpen(false)
                setSubmitting(false)
            })	
  	}

 	return (
		<GoogleOAuthProvider clientId={GOOGLE_APP_ID}>
	    	<Dialog 
	    		open={props.dialogIsOpen} 
	    		onClose={props.handleDialogClose} 
	    		aria-labelledby="form-dialog-login"
	    		fullWidth 
	    		maxWidth="xs"
	    		TransitionComponent={Zoom}
	    	>
				<DialogContent>
					<section className="login_section p-0">
						<div className="container">
							<div className="header-form mb-4">
								<Link to="/" className="mulklogo">
									<img src={logo} alt="Mulk.pk" />
								</Link>
								<h3>Login with Mulk</h3>
								{/* <p>Connecting local businesses online.</p> */}
								<h4>اپنا ملک اپنی بات</h4>
							</div>
							
							<div className="soc-log fl-wrap">
								<p>For faster login use your social account.</p>
								<div className="row">
									<div className="col-md-12 col-sm-12 col-xs-12 mb-1">
										<FacebookLoginBtn 
											isModal={true}
											setOpen={setOpen}
										/>
									</div>
									<div className="col-md-12 col-sm-12 col-xs-12">
										<GoogleLoginBtn 
											isModal={true}
											setOpen={setOpen}
										/>
									</div>
								</div>
							</div>
							
							<div className="log-separator fl-wrap mb-2"><span>or</span></div>
							
							{errors && (
								<ErrorValidationAlert
									errors={errors} 
								/>
							)}

							{message && (
								<ErrorMessage
									message={message} 
								/>
							)}
						
							<Formik 
								initialValues={{ 
									login: "", 
									password: "",
									terms: true
								}}
								validationSchema={formSchema}
								onSubmit={handleSubmit}
							>
								{formik => (
									<form onSubmit={formik.handleSubmit}>
										<div className="row mt-1 mb-1">
											<div className="col-md-12 col-sm-12 col-xs-12">
												<TextField 
													id="login" 
													label="Mobile number or email address"
													variant="outlined" 
													size="small"
													fullWidth
													{...formik.getFieldProps('login')} 
													error={formik.errors.login ? true : false}
													helperText="e.g. +923001234567 or youremail@domain.com"
												/> 
												{formik.errors.login &&
													<FormHelperText error={true}>{formik.errors.login}</FormHelperText>
												}
											</div>
										</div>
										<div className="row">
											<div className="col-md-12 col-sm-12 col-xs-12">
												<TextField
													label="Password"
													id="password-pc"
													type={values.showPassword ? 'text' : 'password'}
													fullWidth
													{...formik.getFieldProps('password')}
													variant="outlined"
													size="small"
													error={formik.errors.password ? true : false}
													InputProps={{
														endAdornment: 
															<InputAdornment position="end">
																<IconButton
																	aria-label="toggle password visibility"
																	onClick={handleClickShowPassword}
																	onMouseDown={handleMouseDownPassword}
																>
																	{values.showPassword ?<Icon>visibility</Icon> : <Icon>visibility_off</Icon>}
																</IconButton>
															</InputAdornment>,
													}}
												/>
												{formik.errors.password && 
													<FormHelperText error={true}>{formik.errors.password}</FormHelperText>
												}
											</div>
										</div>
										<div className="row mt-1 mb-1">
											<div className="col-md-12 col-sm-12 col-xs-12 text-left">
												<FormControlLabel
													control={<Checkbox 
														name="terms" 
														size="small"
														color="primary"
														checked={formik.values.terms}
														onChange={() => {
															formik.setFieldValue('terms', !formik.values.terms, true)
														}}  
													/>}
													label={
														<div className={classes.formControlLabel}>
															<span>I agree to mulk.pk</span>&nbsp; 
															<Link className="span-link" to="/user-terms-of-service">terms and conditions</Link>.
														</div>
													}
													name="terms"
													value={true}
													//style={{ float: 'left', color: 'rgba(0, 0, 0, 0.6)' }}
												/>
												{formik.errors.terms &&
													<FormHelperText error={true}>{formik.errors.terms}</FormHelperText>
												}
											</div>
										</div>
										<div className="row">
											<div className="col-md-12 col-sm-12 col-xs-12">
												<Button
													variant="contained"
													color="primary"
												//    className={classes.button}
													endIcon={<Icon>vpn_key</Icon>}
													fullWidth
													type="submit"
													disabled={formik.isSubmitting}
												>
													{formik.isSubmitting && <CircularProgress size={24} />}
													{!formik.isSubmitting && 'Log In'}
												</Button>
											</div>
										</div>
									</form>
								)}

							</Formik>
							
							<div className="login_footer mt-2 mb-1">
								<p>Don't have an account?</p>
								<Link to="/register" className="crate_btn">
									Create account
								</Link>
							</div>
						</div>
					</section>
					<Backdrop className={classes.backdrop} open={open}>
						<CircularProgress color="inherit" />
					</Backdrop>
				</DialogContent>
		        <DialogActions className="dialog-footer">
		          	<Button onClick={props.handleDialogClose} color="primary">
		            	Cancel
		          	</Button>
		        </DialogActions>
			</Dialog>
		</GoogleOAuthProvider>
  	)
}

export default LoginDialog;